import cn from 'classnames';
import { FlaggableIngredient, MealPlanFoodItemUpsellFragment, MerchandisingTag } from 'src/gqlReactTypings.generated.d';
import { generateThumborImgUrl } from 'src/shared/design-system/DynamicImage';
import { FoodItemChip } from '../Chips/FoodItemChip';
import { IngredientsFlaggedChip } from '../Chips/IngredientsFlaggedChip';
import { MerchandisingTagChip } from '../Chips/MerchandisingTagChip';
import { UpsellLabelChip } from '../Chips/UpsellChips';

export enum DishImageAspectRatio {
  Standard = 'standard',
  Tall = 'tall',
}

/**
 * Applied as classes rather than an inline `aspect-ratio`, because `Tall` has to
 * change at the `md` breakpoint and an inline style cannot.
 */
const CLASS_BY_ASPECT_RATIO: Record<DishImageAspectRatio, string> = {
  [DishImageAspectRatio.Standard]: 'aspect-[3/2]',
  [DishImageAspectRatio.Tall]: 'aspect-[4/3] md:aspect-[5/4]',
};

interface MealPlanDishImageProps {
  imageUrl: string;
  height: number;
  width: number;
  remainingAvailableUnits: number;
  unitsInCart: number;
  upsell?: MealPlanFoodItemUpsellFragment | null;
  ingredientsFlagged?: FlaggableIngredient[];
  customIngredientsFlagged?: string[] | null;
  hideIngredientsFlaggedChip?: boolean;
  merchandisingTags?: MerchandisingTag[];
  showNewFreshTag?: boolean;
  className?: string;
  aspectRatio?: DishImageAspectRatio;
  favoriteHeart?: React.ReactElement;
  zoom?: number;
  bottomLeftChip?: React.ReactNode;
}

export const MealPlanDishImage: React.FC<MealPlanDishImageProps> = ({
  className,
  bottomLeftChip,
  imageUrl,
  remainingAvailableUnits,
  unitsInCart,
  height,
  width,
  upsell,
  ingredientsFlagged,
  customIngredientsFlagged,
  hideIngredientsFlaggedChip = false,
  merchandisingTags,
  showNewFreshTag,
  aspectRatio,
  favoriteHeart,
  zoom = 1,
}) => {
  const isInCart = unitsInCart > 0;
  const disabled = !isInCart && remainingAvailableUnits <= 0;
  const renderGreyOverlay = <div className='absolute left-0 top-0 size-full bg-white opacity-60' />;

  const upsellChip = <UpsellLabelChip upsell={upsell} />;
  const foodItemChip = (
    <div className='absolute left-2 top-2'>
      <FoodItemChip remainingAvailableUnits={remainingAvailableUnits} unitsInCart={unitsInCart} />
    </div>
  );
  const ingredientsFlaggedChip = !hideIngredientsFlaggedChip && ingredientsFlagged && (
    <IngredientsFlaggedChip
      ingredientsFlagged={ingredientsFlagged}
      customIngredientsFlagged={customIngredientsFlagged ?? []}
    />
  );
  // Negative offsets cancel the heart's 48px tap target so the 32px icon hugs the corner.
  const topRightStack = (favoriteHeart || ingredientsFlaggedChip) && (
    <div className={cn('absolute flex items-center gap-1', favoriteHeart ? '-right-1 -top-1' : 'right-2 top-2')}>
      {ingredientsFlaggedChip}
      {favoriteHeart}
    </div>
  );
  const merchandisingTagChip = merchandisingTags && (
    <div className='absolute bottom-2 right-2'>
      <MerchandisingTagChip merchandisingTags={merchandisingTags ?? []} showNewFreshTag={showNewFreshTag} />
    </div>
  );

  const isZoomed = zoom > 1;
  const imageSrc = generateThumborImgUrl({
    src: imageUrl,
    width: Math.ceil(width * zoom),
    height: Math.ceil(height * zoom),
  });

  const aspectRatioClass = aspectRatio && CLASS_BY_ASPECT_RATIO[aspectRatio];

  /** Only for zoomed callers that give the box neither a ratio nor a height. */
  const fallbackAspectRatio = !aspectRatio && isZoomed ? `${width} / ${height}` : undefined;

  return (
    <div
      className={cn('relative w-full', aspectRatioClass, { 'shrink-0': isZoomed }, className)}
      style={{ aspectRatio: fallbackAspectRatio }}>
      {isZoomed ? (
        <div className='absolute inset-0 overflow-hidden'>
          <img
            className='size-full object-cover object-center'
            style={{ transform: `scale(${zoom})` }}
            src={imageSrc}
            alt=''
          />
        </div>
      ) : (
        <img className={cn('size-full shrink-0 object-cover object-center', aspectRatioClass)} src={imageSrc} alt='' />
      )}
      {disabled && renderGreyOverlay}
      <>
        {foodItemChip}
        {(upsell || bottomLeftChip) && (
          <div className='absolute bottom-2 left-2 flex items-center gap-1 empty:hidden'>
            {upsell && upsellChip}
            {bottomLeftChip}
          </div>
        )}
        {topRightStack}
        {merchandisingTagChip}
      </>
    </div>
  );
};
